import React, { useState, useRef } from "react"
import SimpleBar from "simplebar-react"
import { withRouter } from "react-router-dom"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { sideBarContent, roleBasedSequence } from "./SidebarConfig"
import { APP_ROUTES } from "helpers/routeHelpers"
import { sortSidebarByRoleSequence } from "../../utils/commonUtils"

const getMainRouteFromPath = path => {
   const parts = path.split("/").filter(Boolean)
   return parts.length ? `/${parts[0]}` : "/"
}

const pathMatches = (base, path) => {
   return path === base || path.startsWith(base + "/")
}

const SidebarContent = props => {
   const userType = useSelector(state => state?.userAuth?.user?.type)
   const [expandedMenus, setExpandedMenus] = useState({})
   const currentPath = props.location.pathname
   const ref = useRef()

   const toggleSubmenu = (menuIndex, currentExpandedState) => {
      setExpandedMenus(prev => ({
         ...prev,
         [menuIndex]: !currentExpandedState,
      }))
   }

   const handleMenuClick = (item, index) => {
      if (item.hasSubmenu) {
         props.history.push(item.routeName)
         toggleSubmenu(index)
      } else {
         props.history.push(item.routeName)
      }
   }

   const isMenuActive = item => {
      if (item.hasSubmenu) {
         const currentMain = getMainRouteFromPath(currentPath)
         const itemMain = getMainRouteFromPath(item.routeName)
         return currentMain === itemMain
      }
      return pathMatches(item.routeName, currentPath)
   }

   const isSubmenuItemActive = submenuItem => {
      return currentPath.includes(submenuItem.routeName)
   }

   const isOnSubmenuRoute = item => {
      return (
         item.hasSubmenu &&
         item.submenuItems &&
         item.submenuItems.some(subItem =>
            currentPath.includes(subItem.routeName)
         )
      )
   }

   const shouldSubmenuBeExpanded = (item, idx) => {
      if (expandedMenus.hasOwnProperty(idx)) {
         return expandedMenus[idx]
      }
      return isOnSubmenuRoute(item)
   }

   let sidebarContentToRender = sideBarContent.filter(obj =>
      obj.permissions.includes(userType)
   )

   sidebarContentToRender = sortSidebarByRoleSequence(
      sidebarContentToRender,
      roleBasedSequence,
      userType
   )

   return (
      <SimpleBar className="h-100" ref={ref}>
         <div id="sidebar-menu" className="gt-side-menu">
            <ul className="metismenu list-unstyled" id="side-menu">
               {sidebarContentToRender.map((item, idx) => (
                  <li
                     key={idx}
                     className={`menu-container text-center ${
                        item.liClasses || ""
                     } ${isMenuActive(item) ? "mm-active" : ""} ${
                        item.hasSubmenu && shouldSubmenuBeExpanded(item, idx)
                           ? "has-expanded-submenu"
                           : ""
                     }`}
                     data-route={
                        item.routeName === APP_ROUTES.CONFIGURATOR_TRAILERS
                           ? "configurator"
                           : ""
                     }
                     data-index={idx}
                  >
                     {item.hasSubmenu ? (
                        <>
                           <div
                              className={`menu-item-with-submenu ${
                                 item.routeClasses || ""
                              }`}
                              onClick={e => {
                                 if (item.hasSubmenu) {
                                    props.history.push(item.routeName)
                                 } else {
                                    props.history.push(item.routeName)
                                 }
                              }}
                              style={{ cursor: "pointer" }}
                           >
                              <img
                                 src={item.image}
                                 className={`img-fluid cs-menu-icon ${
                                    item.imageClasses || ""
                                 }`}
                                 alt={item.title}
                              />
                              <span
                                 className={`font-size-13 text-center ${
                                    item.titleClasses || ""
                                 }`}
                              >
                                 {item.title}
                              </span>
                              <i
                                 className="fas fa-chevron-down chevron-icon"
                                 style={{
                                    transition: "transform 0.3s ease",
                                    transform: shouldSubmenuBeExpanded(
                                       item,
                                       idx
                                    )
                                       ? "rotate(180deg)"
                                       : "rotate(0deg)",
                                 }}
                                 onClick={e => {
                                    e.stopPropagation()
                                    toggleSubmenu(
                                       idx,
                                       shouldSubmenuBeExpanded(item, idx)
                                    )
                                 }}
                              />
                           </div>
                           <div
                              className={`submenu-container ${
                                 shouldSubmenuBeExpanded(item, idx)
                                    ? "submenu-expanded"
                                    : "submenu-collapsed"
                              }`}
                              style={{
                                 maxHeight: shouldSubmenuBeExpanded(item, idx)
                                    ? "500px"
                                    : "0",
                                 overflow: "hidden",
                                 transition:
                                    "max-height 0.3s ease, opacity 0.3s ease",
                                 opacity: shouldSubmenuBeExpanded(item, idx)
                                    ? 1
                                    : 0,
                              }}
                           >
                              <ul className="submenu-list">
                                 {item.submenuItems
                                    .filter(subItem =>
                                       subItem.permissions.includes(userType)
                                    )
                                    .map((subItem, subIdx) => (
                                       <li
                                          key={subIdx}
                                          className={`submenu-item ${
                                             isSubmenuItemActive(subItem)
                                                ? "active"
                                                : ""
                                          }`}
                                       >
                                          <Link
                                             to={subItem.routeName}
                                             className={`submenu-link ${
                                                subItem.routeClasses || ""
                                             }`}
                                             onClick={e => e.stopPropagation()}
                                          >
                                             <img
                                                src={subItem.image}
                                                className={`img-fluid cs-menu-icon submenu-icon ${
                                                   subItem.imageClasses || ""
                                                }`}
                                                alt={subItem.title}
                                             />
                                             <span
                                                className={`font-size-12 submenu-title ${
                                                   subItem.titleClasses || ""
                                                }`}
                                             >
                                                {subItem.title}
                                             </span>
                                          </Link>
                                       </li>
                                    ))}
                              </ul>
                           </div>
                        </>
                     ) : (
                        <Link
                           to={item.routeName}
                           className={`${item.routeClasses || ""}`}
                           onClick={() => handleMenuClick(item, idx)}
                        >
                           <img
                              src={item.image}
                              className={`img-fluid cs-menu-icon ${
                                 item.imageClasses || ""
                              }`}
                              alt={item.title}
                           />
                           <span
                              className={`font-size-13 text-center ${
                                 item.titleClasses || ""
                              }`}
                           >
                              {item.title}
                           </span>
                        </Link>
                     )}
                  </li>
               ))}
            </ul>
         </div>
      </SimpleBar>
   )
}

export default withRouter(withTranslation()(SidebarContent))
